import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Users, BookOpen, Settings, Shield, TrendingUp, UserPlus, Key, Database, Mail } from 'lucide-react';

interface User {
  ID: number;
  Name: string;
  Email: string;
  CreateTime: string;
  IsActivated?: boolean;
}

interface AdminUser {
  id: number;
  user_id: number;
  admin_role: string;
  assigned_by: number;
  assigned_at: string;
  is_active: boolean;
}

interface UserTier {
  id: number;
  user_id: number;
  tier_level: number;
  tier_name: string;
  assigned_by: number;
  assigned_at: string;
}

interface AppSetting {
  id: number;
  setting_key: string;
  setting_value: string;
  setting_description: string;
  updated_by: number;
  updated_at: string;
}

interface Class {
  id: number;
  class_name: string;
  subject: string;
  description: string;
  teacher_id: number;
  class_code: string;
  semester: string;
  is_active: boolean;
}

const AdminDashboard: React.FC = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // State for different data types
  const [users, setUsers] = useState<User[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [userTiers, setUserTiers] = useState<UserTier[]>([]);
  const [appSettings, setAppSettings] = useState<AppSetting[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);

  // State for forms
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newAdminUser, setNewAdminUser] = useState({ user_id: '', admin_role: 'Administrator' });
  const [newUserTier, setNewUserTier] = useState({ user_id: '', tier_level: 1, tier_name: 'Basic' });
  const [newSetting, setNewSetting] = useState({ setting_key: '', setting_value: '', setting_description: '' });

  // Statistics state
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    totalClasses: 0,
    activeClasses: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
      loadAllUsers(),
      loadAdminUsers(),
      loadUserTiers(),
      loadAppSettings(),
      loadClasses()]
      );
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      // Try to fetch users from a hypothetical user management API
      // Since we don't have direct access to all auth users, we'll check user_profiles table
      // and admin_users table to get an idea of total users
      const { data: profilesData, error: profilesError } = await window.ezsite.apis.tablePage(16056, {
        PageNo: 1,
        PageSize: 1000, // Get a large number to count all users
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });

      if (profilesError) {
        console.error('Error loading user profiles:', profilesError);
      }

      // Get unique user IDs from profiles and admin tables
      const userIds = new Set<number>();

      // Add user IDs from profiles
      if (profilesData?.List) {
        profilesData.List.forEach((profile: any) => {
          if (profile.user_id) {
            userIds.add(profile.user_id);
          }
        });
      }

      // Add user IDs from admin users
      if (adminUsers.length > 0) {
        adminUsers.forEach((admin) => {
          userIds.add(admin.user_id);
        });
      }

      // Get current user info
      const { data: currentUser, error: currentUserError } = await window.ezsite.apis.getUserInfo();
      if (!currentUserError && currentUser) {
        userIds.add(currentUser.ID);
        setUsers([currentUser]); // For display purposes, we'll just show current user
      }

      // Update total users count based on unique user IDs
      const totalUsers = userIds.size;
      setStats((prev) => ({ ...prev, totalUsers }));

      console.log('Total unique users found:', totalUsers);

    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback: just get current user
      try {
        const { data, error } = await window.ezsite.apis.getUserInfo();
        if (error) throw error;
        setUsers([data]);
        setStats((prev) => ({ ...prev, totalUsers: 1 }));
      } catch (fallbackError) {
        console.error('Error loading current user:', fallbackError);
        setStats((prev) => ({ ...prev, totalUsers: 0 }));
      }
    }
  };

  const loadAdminUsers = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15992, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });
      if (error) throw error;
      setAdminUsers(data.List || []);
      setStats((prev) => ({ ...prev, totalAdmins: data.List?.length || 0 }));
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  const loadUserTiers = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15216, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });
      if (error) throw error;
      setUserTiers(data.List || []);
    } catch (error) {
      console.error('Error loading user tiers:', error);
    }
  };

  const loadAppSettings = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15215, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });
      if (error) throw error;
      setAppSettings(data.List || []);
    } catch (error) {
      console.error('Error loading app settings:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15198, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });
      if (error) throw error;
      setClasses(data.List || []);
      const activeCount = data.List?.filter((cls: Class) => cls.is_active).length || 0;
      setStats((prev) => ({
        ...prev,
        totalClasses: data.List?.length || 0,
        activeClasses: activeCount
      }));
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminUser.user_id) {
      toast({
        title: "Error",
        description: "Please select a user",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await window.ezsite.apis.tableCreate(15992, {
        user_id: parseInt(newAdminUser.user_id),
        admin_role: newAdminUser.admin_role,
        assigned_by: 1, // Current admin's ID
        assigned_at: new Date().toISOString(),
        is_active: true
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Admin user added successfully"
      });

      setNewAdminUser({ user_id: '', admin_role: 'Administrator' });
      loadAdminUsers();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: "Error",
        description: "Failed to add admin user",
        variant: "destructive"
      });
    }
  };

  const handleRemoveAdmin = async (adminId: number) => {
    try {
      const { error } = await window.ezsite.apis.tableDelete(15992, { ID: adminId });
      if (error) throw error;

      toast({
        title: "Success",
        description: "Admin user removed successfully"
      });

      loadAdminUsers();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove admin user",
        variant: "destructive"
      });
    }
  };

  const handleUpdateUserTier = async () => {
    if (!newUserTier.user_id) {
      toast({
        title: "Error",
        description: "Please select a user",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await window.ezsite.apis.tableCreate(15216, {
        user_id: parseInt(newUserTier.user_id),
        tier_level: newUserTier.tier_level,
        tier_name: newUserTier.tier_name,
        assigned_by: 1, // Current admin's ID
        assigned_at: new Date().toISOString()
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User tier updated successfully"
      });

      setNewUserTier({ user_id: '', tier_level: 1, tier_name: 'Basic' });
      loadUserTiers();
    } catch (error) {
      console.error('Error updating user tier:', error);
      toast({
        title: "Error",
        description: "Failed to update user tier",
        variant: "destructive"
      });
    }
  };

  const handleAddSetting = async () => {
    if (!newSetting.setting_key || !newSetting.setting_value) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await window.ezsite.apis.tableCreate(15215, {
        setting_key: newSetting.setting_key,
        setting_value: newSetting.setting_value,
        setting_description: newSetting.setting_description,
        updated_by: 1, // Current admin's ID
        updated_at: new Date().toISOString()
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Setting added successfully"
      });

      setNewSetting({ setting_key: '', setting_value: '', setting_description: '' });
      loadAppSettings();
    } catch (error) {
      console.error('Error adding setting:', error);
      toast({
        title: "Error",
        description: "Failed to add setting",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-id="8qmisz8qy" data-path="src/components/dashboard/AdminDashboard.tsx">
      <div className="flex items-center justify-between" data-id="edauuozq7" data-path="src/components/dashboard/AdminDashboard.tsx">
        <div data-id="a0slt3ehf" data-path="src/components/dashboard/AdminDashboard.tsx">
          <h1 className="text-3xl font-bold" data-id="qrfv7o3cu" data-path="src/components/dashboard/AdminDashboard.tsx">Admin Dashboard</h1>
          <p className="text-muted-foreground" data-id="vw40ukmx6" data-path="src/components/dashboard/AdminDashboard.tsx">Manage users, settings, and system configuration</p>
        </div>
        <Badge variant="secondary" className="px-3 py-1" data-id="dqvjmet8h" data-path="src/components/dashboard/AdminDashboard.tsx">
          <Shield className="h-4 w-4 mr-1" data-id="q3e8zji5d" data-path="src/components/dashboard/AdminDashboard.tsx" />
          Administrator
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-id="5t3i8cjec" data-path="src/components/dashboard/AdminDashboard.tsx">
        <TabsList className="grid w-full grid-cols-5" data-id="2n2o9vklr" data-path="src/components/dashboard/AdminDashboard.tsx">
          <TabsTrigger value="overview" data-id="idf1cvlv0" data-path="src/components/dashboard/AdminDashboard.tsx">Overview</TabsTrigger>
          <TabsTrigger value="users" data-id="5wo7m56j9" data-path="src/components/dashboard/AdminDashboard.tsx">Users</TabsTrigger>
          <TabsTrigger value="admins" data-id="lw1fqnbij" data-path="src/components/dashboard/AdminDashboard.tsx">Admins</TabsTrigger>
          <TabsTrigger value="tiers" data-id="k44liyyzh" data-path="src/components/dashboard/AdminDashboard.tsx">User Tiers</TabsTrigger>
          <TabsTrigger value="settings" data-id="141wymqce" data-path="src/components/dashboard/AdminDashboard.tsx">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6" data-id="px9k7vouc" data-path="src/components/dashboard/AdminDashboard.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="ursirqpnx" data-path="src/components/dashboard/AdminDashboard.tsx">
            <Card data-id="ndelnrkxa" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="kny1tzal6" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle className="text-sm font-medium" data-id="alfquvesr" data-path="src/components/dashboard/AdminDashboard.tsx">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" data-id="giqna7l7f" data-path="src/components/dashboard/AdminDashboard.tsx" />
              </CardHeader>
              <CardContent data-id="56k044qai" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="text-2xl font-bold" data-id="mlpi0bl97" data-path="src/components/dashboard/AdminDashboard.tsx">{stats.totalUsers}</div>
                <p className="text-xs text-muted-foreground" data-id="ifmwgmadu" data-path="src/components/dashboard/AdminDashboard.tsx">Registered users</p>
              </CardContent>
            </Card>

            <Card data-id="pe6ueywou" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="fkq2huzsz" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle className="text-sm font-medium" data-id="x7rsqiol1" data-path="src/components/dashboard/AdminDashboard.tsx">Admin Users</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" data-id="qwccfgsi6" data-path="src/components/dashboard/AdminDashboard.tsx" />
              </CardHeader>
              <CardContent data-id="ehg76hv26" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="text-2xl font-bold" data-id="s4ekoooks" data-path="src/components/dashboard/AdminDashboard.tsx">{stats.totalAdmins}</div>
                <p className="text-xs text-muted-foreground" data-id="31h6yusei" data-path="src/components/dashboard/AdminDashboard.tsx">Active administrators</p>
              </CardContent>
            </Card>

            <Card data-id="ykr0indvc" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="kxw1vgrx5" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle className="text-sm font-medium" data-id="6yzwcp3gb" data-path="src/components/dashboard/AdminDashboard.tsx">Total Classes</CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" data-id="dnj6uaaei" data-path="src/components/dashboard/AdminDashboard.tsx" />
              </CardHeader>
              <CardContent data-id="prt5dxe50" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="text-2xl font-bold" data-id="tbxwazeuy" data-path="src/components/dashboard/AdminDashboard.tsx">{stats.totalClasses}</div>
                <p className="text-xs text-muted-foreground" data-id="aui3ucazg" data-path="src/components/dashboard/AdminDashboard.tsx">All classes</p>
              </CardContent>
            </Card>

            <Card data-id="xsofqmsrh" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="ud9fc29p0" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle className="text-sm font-medium" data-id="rfy2vz507" data-path="src/components/dashboard/AdminDashboard.tsx">Active Classes</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" data-id="7cn79hws6" data-path="src/components/dashboard/AdminDashboard.tsx" />
              </CardHeader>
              <CardContent data-id="l2yj2hlny" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="text-2xl font-bold" data-id="0vloxb5zv" data-path="src/components/dashboard/AdminDashboard.tsx">{stats.activeClasses}</div>
                <p className="text-xs text-muted-foreground" data-id="azezyqnzn" data-path="src/components/dashboard/AdminDashboard.tsx">Currently active</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="siad988r4" data-path="src/components/dashboard/AdminDashboard.tsx">
            <Card data-id="xoui4xpkr" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader data-id="lzxf4n12j" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle data-id="l2nhiujvu" data-path="src/components/dashboard/AdminDashboard.tsx">Recent Classes</CardTitle>
                <CardDescription data-id="jdqutcttm" data-path="src/components/dashboard/AdminDashboard.tsx">Latest classes created in the system</CardDescription>
              </CardHeader>
              <CardContent data-id="kdq30kuh8" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="space-y-4" data-id="y0j1b6b6h" data-path="src/components/dashboard/AdminDashboard.tsx">
                  {classes.slice(0, 5).map((cls) =>
                  <div key={cls.id} className="flex items-center justify-between" data-id="yf96rrxwr" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <div data-id="zubmwcuy1" data-path="src/components/dashboard/AdminDashboard.tsx">
                        <p className="font-medium" data-id="mt36jf8ce" data-path="src/components/dashboard/AdminDashboard.tsx">{cls.class_name}</p>
                        <p className="text-sm text-muted-foreground" data-id="81c5eum9x" data-path="src/components/dashboard/AdminDashboard.tsx">{cls.subject}</p>
                      </div>
                      <Badge variant={cls.is_active ? "default" : "secondary"} data-id="2piynwpbs" data-path="src/components/dashboard/AdminDashboard.tsx">
                        {cls.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card data-id="wk8y2igwd" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader data-id="gnxrujkfp" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle data-id="p1dedzrt0" data-path="src/components/dashboard/AdminDashboard.tsx">System Health</CardTitle>
                <CardDescription data-id="ja3s1xfgi" data-path="src/components/dashboard/AdminDashboard.tsx">Current system status and metrics</CardDescription>
              </CardHeader>
              <CardContent data-id="0ajq298ow" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="space-y-4" data-id="kdx30fney" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <div className="flex items-center justify-between" data-id="7nhet6t88" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <span data-id="ejo2t9r57" data-path="src/components/dashboard/AdminDashboard.tsx">Database Connection</span>
                    <Badge variant="default" data-id="2nddgketr" data-path="src/components/dashboard/AdminDashboard.tsx">Online</Badge>
                  </div>
                  <div className="flex items-center justify-between" data-id="lys83dqru" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <span data-id="4ycku150e" data-path="src/components/dashboard/AdminDashboard.tsx">API Status</span>
                    <Badge variant="default" data-id="e1ardrd0q" data-path="src/components/dashboard/AdminDashboard.tsx">Operational</Badge>
                  </div>
                  <div className="flex items-center justify-between" data-id="i5193xzf3" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <span data-id="97btfskgg" data-path="src/components/dashboard/AdminDashboard.tsx">Email Service</span>
                    <Badge variant="default" data-id="97bl045g4" data-path="src/components/dashboard/AdminDashboard.tsx">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between" data-id="mh6lxj84a" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <span data-id="b1n45nz2h" data-path="src/components/dashboard/AdminDashboard.tsx">Storage</span>
                    <Badge variant="default" data-id="jn0bq599m" data-path="src/components/dashboard/AdminDashboard.tsx">Available</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-6" data-id="6nd0gq393" data-path="src/components/dashboard/AdminDashboard.tsx">
          <Card data-id="vqzakwvdt" data-path="src/components/dashboard/AdminDashboard.tsx">
            <CardHeader data-id="qmzp2q5mi" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardTitle data-id="5mi4wa2mc" data-path="src/components/dashboard/AdminDashboard.tsx">User Management</CardTitle>
              <CardDescription data-id="no4mfevf7" data-path="src/components/dashboard/AdminDashboard.tsx">View and manage all system users</CardDescription>
            </CardHeader>
            <CardContent data-id="bicb0oj6k" data-path="src/components/dashboard/AdminDashboard.tsx">
              <Table data-id="v5vaannle" data-path="src/components/dashboard/AdminDashboard.tsx">
                <TableHeader data-id="xjx54nb9a" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <TableRow data-id="gmtr6bx8a" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <TableHead data-id="slh29yhiz" data-path="src/components/dashboard/AdminDashboard.tsx">ID</TableHead>
                    <TableHead data-id="tb0o1arer" data-path="src/components/dashboard/AdminDashboard.tsx">Name</TableHead>
                    <TableHead data-id="axo8pm5hr" data-path="src/components/dashboard/AdminDashboard.tsx">Email</TableHead>
                    <TableHead data-id="awu3dr94t" data-path="src/components/dashboard/AdminDashboard.tsx">Created</TableHead>
                    <TableHead data-id="ybrhqp8tr" data-path="src/components/dashboard/AdminDashboard.tsx">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="6tezn1baz" data-path="src/components/dashboard/AdminDashboard.tsx">
                  {users.map((user) =>
                  <TableRow key={user.ID} data-id="vgn9n5ed5" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <TableCell data-id="gqwpq5b9m" data-path="src/components/dashboard/AdminDashboard.tsx">{user.ID}</TableCell>
                      <TableCell data-id="k0sdm13j7" data-path="src/components/dashboard/AdminDashboard.tsx">{user.Name}</TableCell>
                      <TableCell data-id="xhpg8fqqy" data-path="src/components/dashboard/AdminDashboard.tsx">{user.Email}</TableCell>
                      <TableCell data-id="jkt4tnhgq" data-path="src/components/dashboard/AdminDashboard.tsx">{new Date(user.CreateTime).toLocaleDateString()}</TableCell>
                      <TableCell data-id="oghs77axo" data-path="src/components/dashboard/AdminDashboard.tsx">
                        <Button variant="outline" size="sm" data-id="bawc9mo60" data-path="src/components/dashboard/AdminDashboard.tsx">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="admins" className="space-y-6" data-id="g7o8vth46" data-path="src/components/dashboard/AdminDashboard.tsx">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="5mxuuos1c" data-path="src/components/dashboard/AdminDashboard.tsx">
            <div className="lg:col-span-2" data-id="wrqnlku5k" data-path="src/components/dashboard/AdminDashboard.tsx">
              <Card data-id="z282vfruo" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardHeader data-id="jc5rx1bdr" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <CardTitle data-id="lkdd0xzv7" data-path="src/components/dashboard/AdminDashboard.tsx">Admin Users</CardTitle>
                  <CardDescription data-id="79dg31x15" data-path="src/components/dashboard/AdminDashboard.tsx">Manage administrator privileges</CardDescription>
                </CardHeader>
                <CardContent data-id="iu1ecfnv0" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Table data-id="s4c81ydx2" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <TableHeader data-id="38q3ab4r8" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <TableRow data-id="shkcpve7i" data-path="src/components/dashboard/AdminDashboard.tsx">
                        <TableHead data-id="c6k8yuixl" data-path="src/components/dashboard/AdminDashboard.tsx">User ID</TableHead>
                        <TableHead data-id="zzkb1gw35" data-path="src/components/dashboard/AdminDashboard.tsx">Role</TableHead>
                        <TableHead data-id="9410gxqp0" data-path="src/components/dashboard/AdminDashboard.tsx">Assigned</TableHead>
                        <TableHead data-id="vpae4lkon" data-path="src/components/dashboard/AdminDashboard.tsx">Status</TableHead>
                        <TableHead data-id="dgbmy40yv" data-path="src/components/dashboard/AdminDashboard.tsx">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-id="i82wgljpx" data-path="src/components/dashboard/AdminDashboard.tsx">
                      {adminUsers.map((admin) =>
                      <TableRow key={admin.id} data-id="3jlc9ewp3" data-path="src/components/dashboard/AdminDashboard.tsx">
                          <TableCell data-id="hjbr7f5o9" data-path="src/components/dashboard/AdminDashboard.tsx">{admin.user_id}</TableCell>
                          <TableCell data-id="34evlw2bt" data-path="src/components/dashboard/AdminDashboard.tsx">{admin.admin_role}</TableCell>
                          <TableCell data-id="0xbes935f" data-path="src/components/dashboard/AdminDashboard.tsx">{new Date(admin.assigned_at).toLocaleDateString()}</TableCell>
                          <TableCell data-id="tvhi80hpz" data-path="src/components/dashboard/AdminDashboard.tsx">
                            <Badge variant={admin.is_active ? "default" : "secondary"} data-id="vx2yhertv" data-path="src/components/dashboard/AdminDashboard.tsx">
                              {admin.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell data-id="x75i5r0hg" data-path="src/components/dashboard/AdminDashboard.tsx">
                            <AlertDialog data-id="8l4li1n8e" data-path="src/components/dashboard/AdminDashboard.tsx">
                              <AlertDialogTrigger asChild data-id="lu2gocx21" data-path="src/components/dashboard/AdminDashboard.tsx">
                                <Button variant="destructive" size="sm" data-id="li9of40e5" data-path="src/components/dashboard/AdminDashboard.tsx">
                                  Remove
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent data-id="heqqmdvgc" data-path="src/components/dashboard/AdminDashboard.tsx">
                                <AlertDialogHeader data-id="yd6tniqls" data-path="src/components/dashboard/AdminDashboard.tsx">
                                  <AlertDialogTitle data-id="o0bftrj0u" data-path="src/components/dashboard/AdminDashboard.tsx">Remove Admin</AlertDialogTitle>
                                  <AlertDialogDescription data-id="ezi893ehs" data-path="src/components/dashboard/AdminDashboard.tsx">
                                    Are you sure you want to remove admin privileges from this user?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter data-id="8wirwxvod" data-path="src/components/dashboard/AdminDashboard.tsx">
                                  <AlertDialogCancel data-id="2u3ohweye" data-path="src/components/dashboard/AdminDashboard.tsx">Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleRemoveAdmin(admin.id)} data-id="dn5dzhdt1" data-path="src/components/dashboard/AdminDashboard.tsx">
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            <Card data-id="hfx21wsya" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader data-id="ncnud6g0r" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle data-id="n2w51dek1" data-path="src/components/dashboard/AdminDashboard.tsx">Add Admin</CardTitle>
                <CardDescription data-id="72bcksaud" data-path="src/components/dashboard/AdminDashboard.tsx">Grant admin privileges to a user</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="qwnvjz1px" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="space-y-2" data-id="28w2cbj8o" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="admin-user" data-id="t0kms4pbn" data-path="src/components/dashboard/AdminDashboard.tsx">User ID</Label>
                  <Input
                    id="admin-user"
                    placeholder="Enter user ID"
                    value={newAdminUser.user_id}
                    onChange={(e) => setNewAdminUser({ ...newAdminUser, user_id: e.target.value })} data-id="47gxs3ych" data-path="src/components/dashboard/AdminDashboard.tsx" />

                </div>
                <div className="space-y-2" data-id="5p35z8zgd" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="admin-role" data-id="hcn8x8rqi" data-path="src/components/dashboard/AdminDashboard.tsx">Role</Label>
                  <Select
                    value={newAdminUser.admin_role}
                    onValueChange={(value) => setNewAdminUser({ ...newAdminUser, admin_role: value })} data-id="g1qzqmu6n" data-path="src/components/dashboard/AdminDashboard.tsx">

                    <SelectTrigger data-id="9c6yhcjx3" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <SelectValue data-id="wpitsdl9m" data-path="src/components/dashboard/AdminDashboard.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="60yu8xjio" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <SelectItem value="Administrator" data-id="zzof6erj2" data-path="src/components/dashboard/AdminDashboard.tsx">Administrator</SelectItem>
                      <SelectItem value="System Admin" data-id="eathnw6uw" data-path="src/components/dashboard/AdminDashboard.tsx">System Admin</SelectItem>
                      <SelectItem value="Content Admin" data-id="lgsq815s9" data-path="src/components/dashboard/AdminDashboard.tsx">Content Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddAdmin} className="w-full" data-id="34962i8tt" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <UserPlus className="h-4 w-4 mr-2" data-id="241eulidd" data-path="src/components/dashboard/AdminDashboard.tsx" />
                  Add Admin
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tiers" className="space-y-6" data-id="xozbn3roi" data-path="src/components/dashboard/AdminDashboard.tsx">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="by144nqov" data-path="src/components/dashboard/AdminDashboard.tsx">
            <div className="lg:col-span-2" data-id="ncjdntj97" data-path="src/components/dashboard/AdminDashboard.tsx">
              <Card data-id="a8g0if9xk" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardHeader data-id="3cr73ts7b" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <CardTitle data-id="q5gbkyal1" data-path="src/components/dashboard/AdminDashboard.tsx">User Tiers</CardTitle>
                  <CardDescription data-id="e77lq57x1" data-path="src/components/dashboard/AdminDashboard.tsx">Manage user access levels and privileges</CardDescription>
                </CardHeader>
                <CardContent data-id="7bdjrlqp0" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Table data-id="h42tdd71u" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <TableHeader data-id="qfn8u8m2l" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <TableRow data-id="cs12k2upf" data-path="src/components/dashboard/AdminDashboard.tsx">
                        <TableHead data-id="oq0vu38lz" data-path="src/components/dashboard/AdminDashboard.tsx">User ID</TableHead>
                        <TableHead data-id="zg4wkktsm" data-path="src/components/dashboard/AdminDashboard.tsx">Tier Level</TableHead>
                        <TableHead data-id="cttdgfil3" data-path="src/components/dashboard/AdminDashboard.tsx">Tier Name</TableHead>
                        <TableHead data-id="9jh7nf62x" data-path="src/components/dashboard/AdminDashboard.tsx">Assigned</TableHead>
                        <TableHead data-id="8sq6ed98p" data-path="src/components/dashboard/AdminDashboard.tsx">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-id="ajspsxbjy" data-path="src/components/dashboard/AdminDashboard.tsx">
                      {userTiers.map((tier) =>
                      <TableRow key={tier.id} data-id="h5wf0j8z5" data-path="src/components/dashboard/AdminDashboard.tsx">
                          <TableCell data-id="c16h2ijdq" data-path="src/components/dashboard/AdminDashboard.tsx">{tier.user_id}</TableCell>
                          <TableCell data-id="lqa8tuhyv" data-path="src/components/dashboard/AdminDashboard.tsx">{tier.tier_level}</TableCell>
                          <TableCell data-id="wksmc84s2" data-path="src/components/dashboard/AdminDashboard.tsx">
                            <Badge variant={tier.tier_level === 2 ? "default" : "secondary"} data-id="ibsb107us" data-path="src/components/dashboard/AdminDashboard.tsx">
                              {tier.tier_name}
                            </Badge>
                          </TableCell>
                          <TableCell data-id="w62xp9cvr" data-path="src/components/dashboard/AdminDashboard.tsx">{new Date(tier.assigned_at).toLocaleDateString()}</TableCell>
                          <TableCell data-id="c2eomjwlf" data-path="src/components/dashboard/AdminDashboard.tsx">
                            <Button variant="outline" size="sm" data-id="vcuxz1o8x" data-path="src/components/dashboard/AdminDashboard.tsx">
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            <Card data-id="ebh2o4cgf" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader data-id="q2pl53y3s" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle data-id="07md42gz8" data-path="src/components/dashboard/AdminDashboard.tsx">Update User Tier</CardTitle>
                <CardDescription data-id="4l3uns891" data-path="src/components/dashboard/AdminDashboard.tsx">Change user access level</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="2d327kce5" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="space-y-2" data-id="7ib2x571b" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="tier-user" data-id="n2kacy1zs" data-path="src/components/dashboard/AdminDashboard.tsx">User ID</Label>
                  <Input
                    id="tier-user"
                    placeholder="Enter user ID"
                    value={newUserTier.user_id}
                    onChange={(e) => setNewUserTier({ ...newUserTier, user_id: e.target.value })} data-id="8b6nnrp16" data-path="src/components/dashboard/AdminDashboard.tsx" />

                </div>
                <div className="space-y-2" data-id="ziefjw96b" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="tier-level" data-id="pxtaa4zr3" data-path="src/components/dashboard/AdminDashboard.tsx">Tier Level</Label>
                  <Select
                    value={newUserTier.tier_level.toString()}
                    onValueChange={(value) => {
                      const level = parseInt(value);
                      setNewUserTier({
                        ...newUserTier,
                        tier_level: level,
                        tier_name: level === 2 ? "Premium" : "Basic"
                      });
                    }} data-id="j61sfb2g1" data-path="src/components/dashboard/AdminDashboard.tsx">

                    <SelectTrigger data-id="sq22pifii" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <SelectValue data-id="on5fyhdas" data-path="src/components/dashboard/AdminDashboard.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="jz7wotc40" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <SelectItem value="1" data-id="gbcqtgw70" data-path="src/components/dashboard/AdminDashboard.tsx">1 - Basic</SelectItem>
                      <SelectItem value="2" data-id="6h882ynwd" data-path="src/components/dashboard/AdminDashboard.tsx">2 - Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleUpdateUserTier} className="w-full" data-id="8xnkg8owp" data-path="src/components/dashboard/AdminDashboard.tsx">
                  Update Tier
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6" data-id="8y3s9l6lm" data-path="src/components/dashboard/AdminDashboard.tsx">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="q0yrxn54z" data-path="src/components/dashboard/AdminDashboard.tsx">
            <div className="lg:col-span-2" data-id="kwegmd04f" data-path="src/components/dashboard/AdminDashboard.tsx">
              <Card data-id="w6bppptf9" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardHeader data-id="8uffx1wem" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <CardTitle data-id="da99zi5xj" data-path="src/components/dashboard/AdminDashboard.tsx">System Settings</CardTitle>
                  <CardDescription data-id="xotaiknx8" data-path="src/components/dashboard/AdminDashboard.tsx">Configure application settings and parameters</CardDescription>
                </CardHeader>
                <CardContent data-id="2z6q08ktv" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Table data-id="09pueg2mf" data-path="src/components/dashboard/AdminDashboard.tsx">
                    <TableHeader data-id="t0wgqej75" data-path="src/components/dashboard/AdminDashboard.tsx">
                      <TableRow data-id="vgu4415vu" data-path="src/components/dashboard/AdminDashboard.tsx">
                        <TableHead data-id="0ksgene82" data-path="src/components/dashboard/AdminDashboard.tsx">Setting Key</TableHead>
                        <TableHead data-id="oxbibk8pi" data-path="src/components/dashboard/AdminDashboard.tsx">Value</TableHead>
                        <TableHead data-id="lo0j08dys" data-path="src/components/dashboard/AdminDashboard.tsx">Description</TableHead>
                        <TableHead data-id="0b1p6hcn9" data-path="src/components/dashboard/AdminDashboard.tsx">Updated</TableHead>
                        <TableHead data-id="9wp462pjl" data-path="src/components/dashboard/AdminDashboard.tsx">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-id="o2gdwqdau" data-path="src/components/dashboard/AdminDashboard.tsx">
                      {appSettings.map((setting) =>
                      <TableRow key={setting.id} data-id="gwyo96rxt" data-path="src/components/dashboard/AdminDashboard.tsx">
                          <TableCell className="font-mono text-sm" data-id="vxilpw1x4" data-path="src/components/dashboard/AdminDashboard.tsx">{setting.setting_key}</TableCell>
                          <TableCell className="max-w-32 truncate" data-id="85bn33opb" data-path="src/components/dashboard/AdminDashboard.tsx">{setting.setting_value}</TableCell>
                          <TableCell className="max-w-48 truncate" data-id="6cyd29lk8" data-path="src/components/dashboard/AdminDashboard.tsx">{setting.setting_description}</TableCell>
                          <TableCell data-id="864s6fvn7" data-path="src/components/dashboard/AdminDashboard.tsx">{new Date(setting.updated_at).toLocaleDateString()}</TableCell>
                          <TableCell data-id="vrpre72st" data-path="src/components/dashboard/AdminDashboard.tsx">
                            <Button variant="outline" size="sm" data-id="taj71t7y6" data-path="src/components/dashboard/AdminDashboard.tsx">
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            <Card data-id="df5h4gb87" data-path="src/components/dashboard/AdminDashboard.tsx">
              <CardHeader data-id="p1l7iy1hj" data-path="src/components/dashboard/AdminDashboard.tsx">
                <CardTitle data-id="x1ok74vou" data-path="src/components/dashboard/AdminDashboard.tsx">Add Setting</CardTitle>
                <CardDescription data-id="c31ww06kh" data-path="src/components/dashboard/AdminDashboard.tsx">Create a new system setting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="c4vwnunay" data-path="src/components/dashboard/AdminDashboard.tsx">
                <div className="space-y-2" data-id="k60lqz56z" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="setting-key" data-id="4sppuvpqx" data-path="src/components/dashboard/AdminDashboard.tsx">Setting Key</Label>
                  <Input
                    id="setting-key"
                    placeholder="e.g., api_rate_limit"
                    value={newSetting.setting_key}
                    onChange={(e) => setNewSetting({ ...newSetting, setting_key: e.target.value })} data-id="gny94ytrw" data-path="src/components/dashboard/AdminDashboard.tsx" />

                </div>
                <div className="space-y-2" data-id="57d761lls" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="setting-value" data-id="vp63f3hed" data-path="src/components/dashboard/AdminDashboard.tsx">Value</Label>
                  <Input
                    id="setting-value"
                    placeholder="Setting value"
                    value={newSetting.setting_value}
                    onChange={(e) => setNewSetting({ ...newSetting, setting_value: e.target.value })} data-id="94xwbrm22" data-path="src/components/dashboard/AdminDashboard.tsx" />

                </div>
                <div className="space-y-2" data-id="v2t3owu1i" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Label htmlFor="setting-description" data-id="a7kn9bbac" data-path="src/components/dashboard/AdminDashboard.tsx">Description</Label>
                  <Input
                    id="setting-description"
                    placeholder="Describe this setting"
                    value={newSetting.setting_description}
                    onChange={(e) => setNewSetting({ ...newSetting, setting_description: e.target.value })} data-id="xz5rt3046" data-path="src/components/dashboard/AdminDashboard.tsx" />

                </div>
                <Button onClick={handleAddSetting} className="w-full" data-id="hk9zrtg9k" data-path="src/components/dashboard/AdminDashboard.tsx">
                  <Settings className="h-4 w-4 mr-2" data-id="tsphvr36g" data-path="src/components/dashboard/AdminDashboard.tsx" />
                  Add Setting
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>);

};

export default AdminDashboard;